import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { ConfigProvider, Popover, Input, DropdownBox } from 'tea-component';
import { DatePickerTrigger } from 'tea-component/es/datepicker/util';
import { useConfig } from 'tea-component/es/_util/config-context';
import { mergeEventProps } from 'tea-component/es/_util/merge-event-props';
import '@/pages/home/components/RangePicker/index.scss';
import HourRangePanel from './HourRangePanel';
import {
  DEFAULT_HOUR_SLOTS,
  DEFAULT_HOUR_TRANSLATION,
  DEFAULT_MAX_HOUR_RANGE_DAYS,
  HOUR_RANGE_DISPLAY_FORMAT,
  HOUR_TRANSLATION_CN,
  createDefaultDisabledDate,
  createHourShortcuts,
  findMatchingShortcutName,
  isValidHourRangeValue,
  normalizeHourRangeValue,
} from './hourRange';
import type {
  IHourRangeTranslation,
  IRangeHourPickerProps,
  THourPanelStep,
  THourRangeValue,
} from './types';

import './index.scss';

export interface IRangeHourPickerRef {
  close: () => void;
  setPopActive: () => void;
}

export {
  clampHourRange,
  formatHourDateTime,
  normalizeHourRangeValue,
  isValidHourRangeValue,
  applyHourSlotToMoment,
  buildHourShortcutRange,
  createHourShortcuts,
  findMatchingShortcutName,
  createDefaultDisabledDate,
  DEFAULT_HOUR_SLOTS,
  DEFAULT_MAX_HOUR_RANGE_DAYS,
  HOUR_RANGE_DISPLAY_FORMAT,
  DEFAULT_HOUR_TRANSLATION,
  HOUR_TRANSLATION_CN,
} from './hourRange';

export type { IHourSlot, IRangeHourPickerProps, THourRangeValue } from './types';

const formatRangeDisplay = (rangeValue: THourRangeValue) => {
  const [start, end] = rangeValue;
  const startStr =
    moment.isMoment(start) && start.isValid()
      ? start.format(HOUR_RANGE_DISPLAY_FORMAT)
      : '';
  const endStr =
    moment.isMoment(end) && end.isValid()
      ? end.format(HOUR_RANGE_DISPLAY_FORMAT)
      : '';
  return { startStr, endStr };
};

const RangeHourPicker = forwardRef<IRangeHourPickerRef, IRangeHourPickerProps>(
  (props, ref) => {
    const {
      value = ['', ''],
      disabled = false,
      className,
      style,
      overlayClassName = '',
      rangePickerClassName,
      rangePrefix: rangePrefixProp,
      showRangePrefix = true,
      language = 'en',
      shortcuts: shortcutsProp,
      disabledDate: disabledDateProp,
      maxRangeDays = DEFAULT_MAX_HOUR_RANGE_DAYS,
      hourSlots = DEFAULT_HOUR_SLOTS,
      changeClose = false,
      startOfWeek = 0,
      onChange,
      translation: translationProp,
      placement = 'bottom-start',
      placementOffset = 14,
      closeOnScroll = true,
    } = props;

    const { classPrefix } = useConfig();

    useEffect(() => {
      moment.updateLocale(moment.locale(), {
        week: {
          dow: startOfWeek || 0,
          doy: 4,
        },
      });
    }, [startOfWeek]);

    const [active, setActive] = useState(false);
    const [panelStep, setPanelStep] = useState<THourPanelStep>('date');
    const [draftValue, setDraftValue] = useState<THourRangeValue>(value);
    const [selectedShortcutName, setSelectedShortcutName] = useState('');

    const shortcuts = useMemo(
      () => shortcutsProp ?? createHourShortcuts(maxRangeDays),
      [shortcutsProp, maxRangeDays],
    );
    const disabledDate = useMemo(
      () => disabledDateProp ?? createDefaultDisabledDate(maxRangeDays),
      [disabledDateProp, maxRangeDays],
    );

    const isCN = ['cn', 'zh_CN'].includes(language);

    const translation: IHourRangeTranslation = useMemo(() => {
      const base = isCN ? HOUR_TRANSLATION_CN : DEFAULT_HOUR_TRANSLATION;
      return { ...base, ...translationProp };
    }, [isCN, translationProp]);

    const rangePrefix = useMemo(() => {
      if (!showRangePrefix) return null;
      if (rangePrefixProp) return rangePrefixProp;
      return (
        <div
          className={
            isCN ? 'dtb-rangepicker-label-prefix-cn' : 'dtb-rangepicker-label-prefix'
          }
        >
          {isCN ? '时' : 'Hour'}
        </div>
      );
    }, [isCN, rangePrefixProp, showRangePrefix]);

    const displayRange = active ? draftValue : value;
    const { startStr, endStr } = useMemo(
      () => formatRangeDisplay(displayRange),
      [displayRange],
    );

    useEffect(() => {
      if (!active) {
        setDraftValue(value);
      }
    }, [value, active]);

    const handleOpen = useCallback(() => {
      if (disabled) return;
      setPanelStep('date');
      setDraftValue(value);
      setSelectedShortcutName('');
      setActive(true);
    }, [disabled, value]);

    const handleClose = useCallback(() => {
      setActive(false);
    }, []);

    useImperativeHandle(ref, () => ({
      close: handleClose,
      setPopActive: handleClose,
    }));

    const emitChange = useCallback(
      (nextValue: THourRangeValue) => {
        const normalized = normalizeHourRangeValue(
          nextValue,
          maxRangeDays,
          hourSlots,
        );
        onChange?.(normalized);
      },
      [hourSlots, maxRangeDays, onChange],
    );

    const handleConfirm = useCallback(() => {
      if (!isValidHourRangeValue(draftValue)) return;
      emitChange(draftValue);
      handleClose();
    }, [draftValue, emitChange, handleClose]);

    const handleShortcutSelect = useCallback(
      (name: string) => {
        const shortcut = shortcuts.find((item) => item.name === name);
        if (!shortcut) return;
        const nextRange = shortcut.onClick() as THourRangeValue;
        setDraftValue(nextRange);
        setSelectedShortcutName(name);
        if (changeClose) {
          emitChange(nextRange);
          handleClose();
        }
      },
      [changeClose, emitChange, handleClose, shortcuts],
    );

    const handleDraftChange = useCallback(
      (nextValue: THourRangeValue) => {
        setDraftValue(nextValue);
        setSelectedShortcutName(findMatchingShortcutName(shortcuts, nextValue));
      },
      [shortcuts],
    );

    const overlayClass = classNames(
      'dtb-rangepicker-overlay',
      'dbi-range-hour-picker-overlay',
      overlayClassName,
    );

    return (
      <ConfigProvider locale={language as 'en'}>
        <Popover
          trigger={[DatePickerTrigger, { onOpen: handleOpen, onClose: handleClose }]}
          visible={active}
          onVisibleChange={setActive}
          placement={placement}
          placementOffset={placementOffset}
          closeOnScroll={closeOnScroll}
          updateOnDimensionChange
          overlay={
            <DropdownBox preventMouseDown adaptable>
              <HourRangePanel
                active={active}
                draftValue={draftValue}
                panelStep={panelStep}
                shortcuts={shortcuts}
                disabledDate={disabledDate}
                maxRangeDays={maxRangeDays}
                hourSlots={hourSlots}
                language={language}
                selectedShortcutName={selectedShortcutName}
                translation={translation}
                onDraftChange={handleDraftChange}
                onPanelStepChange={setPanelStep}
                onShortcutSelect={handleShortcutSelect}
                onConfirm={handleConfirm}
              />
            </DropdownBox>
          }
          overlayClassName={overlayClass}
        >
          <div
            className={classNames(
              { 'dtb-rangepicker-label-wrap': !!rangePrefix },
              'dbi-range-hour-picker',
              `${classPrefix}-datepicker`,
              rangePickerClassName,
              className,
              { 'is-disabled': disabled },
            )}
            style={style}
            {...mergeEventProps(props)}
          >
            <div
              className={classNames(
                `${classPrefix}-datepicker__input`,
                `${classPrefix}-datepicker__input--range`,
                `${classPrefix}-datepicker__input--other`,
              )}
            >
              {rangePrefix}
              <Input
                readOnly
                disabled={disabled}
                placeholder={translation.selectHour}
                value={startStr}
              />
              <span className="dtb-rangepicker-text">~</span>
              <Input
                readOnly
                disabled={disabled}
                placeholder={translation.selectHour}
                value={endStr}
              />
            </div>
          </div>
        </Popover>
      </ConfigProvider>
    );
  },
);

RangeHourPicker.displayName = 'RangeHourPicker';

export default RangeHourPicker;
